import type { Currency, CurrentSearchStage, DateFormat, DisplayMode, OnboardingGoal, OnboardingResponses, SalaryDisplayFormat, TableColumn, ThemeMode, UserSettings } from '../types'
import { supabase } from './supabaseClient'

/**
 * Detect preferred currency from browser locale
 * Maps common locales to supported currencies
 */
const detectCurrencyFromLocale = (): Currency => {
  if (typeof navigator === 'undefined') return 'USD'
  
  const locale = navigator.language || (navigator.languages && navigator.languages[0]) || 'en'
  const lang = locale.split('-')[0].toLowerCase()
  const region = locale.split('-')[1]?.toUpperCase()
  
  // Region-based detection (most accurate)
  const regionToCurrency: Record<string, Currency> = {
    US: 'USD',
    GB: 'GBP',
    UK: 'GBP',
    PL: 'PLN',
    CH: 'CHF',
    CZ: 'CZK',
    SE: 'SEK',
    NO: 'NOK',
    DK: 'DKK',
    CA: 'CAD',
    AU: 'AUD',
    // Eurozone countries
    DE: 'EUR', AT: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR', 
    NL: 'EUR', BE: 'EUR', PT: 'EUR', GR: 'EUR', FI: 'EUR',
    IE: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR',
    LT: 'EUR', MT: 'EUR', CY: 'EUR', LU: 'EUR',
  }
  
  if (region && regionToCurrency[region]) {
    return regionToCurrency[region]
  }
  
  // Language-based fallback
  const langToCurrency: Record<string, Currency> = {
    en: 'USD',  // Default English to USD
    pl: 'PLN',
    cs: 'CZK',
    sv: 'SEK',
    nb: 'NOK', nn: 'NOK', no: 'NOK',
    da: 'DKK',
    // Eurozone languages
    de: 'EUR', fr: 'EUR', es: 'EUR', it: 'EUR', nl: 'EUR',
    pt: 'EUR', el: 'EUR', fi: 'EUR', sk: 'EUR', sl: 'EUR',
    et: 'EUR', lv: 'EUR', lt: 'EUR', mt: 'EUR',
  }
  
  if (langToCurrency[lang]) {
    return langToCurrency[lang]
  }
  
  // Default to USD
  return 'USD'
}

const BASE_SETTINGS: Omit<UserSettings, 'targetCurrency'> = {
  autoConvertRates: true,
  emailIntegrationEnabled: false,
  autoGhostEnabled: true,
  autoArchiveEnabled: true,
  currencyConversionEnabled: false,
  salaryDisplayFormat: 'original',
  displayName: null,
  defaultDisplayMode: 'table',
  dateFormat: 'DD/MM/YYYY',
  visibleColumns: ['favorite', 'company', 'status', 'salary', 'target', 'stages', 'note', 'createdAt'],
  resumeConsentGiven: false,
  resumeConsentDate: null,
  themeMode: 'dark',
  onboardingCompleted: false,
  onboardingGoal: null,
  currentSearchStage: null,
  onboardingResponses: null,
  productTourCompleted: false,
}

const ONBOARDING_GOAL_LABELS: Record<OnboardingGoal, string> = {
  track_applications: 'Track active applications',
  improve_resume: 'Improve my resume',
  prepare_interviews: 'Prepare for interviews',
  compare_offers: 'Compare offers',
  exploring: 'Just exploring',
}

const SEARCH_STAGE_LABELS: Record<CurrentSearchStage, string> = {
  starting: 'Starting soon',
  actively_applying: 'Actively applying',
  interviewing: 'Interviewing',
  considering_offers: 'Considering offers',
  paused: 'Paused for now',
}

const DATE_FORMAT_EXAMPLES: Record<DateFormat, string> = {
  'DD/MM/YYYY': '25/05/2026',
  'MM/DD/YYYY': '05/25/2026',
  'YYYY-MM-DD': '2026-05-25',
}

const buildOnboardingResponses = (settings: UserSettings): OnboardingResponses | null => {
  if (!settings.onboardingCompleted) return settings.onboardingResponses

  return {
    version: 1,
    completedAt: new Date().toISOString(),
    goal: {
      value: settings.onboardingGoal,
      label: settings.onboardingGoal ? ONBOARDING_GOAL_LABELS[settings.onboardingGoal] : null,
    },
    currentSearchStage: {
      value: settings.currentSearchStage,
      label: settings.currentSearchStage ? SEARCH_STAGE_LABELS[settings.currentSearchStage] : null,
    },
    preferences: {
      autoGhost: {
        enabled: settings.autoGhostEnabled,
        label: settings.autoGhostEnabled ? 'Auto-ghost stale applications enabled' : 'Auto-ghost stale applications disabled',
      },
      autoArchive: {
        enabled: settings.autoArchiveEnabled,
        label: settings.autoArchiveEnabled ? 'Auto-archive old applications enabled' : 'Auto-archive old applications disabled',
      },
      dateFormat: {
        value: settings.dateFormat,
        label: settings.dateFormat,
        example: DATE_FORMAT_EXAMPLES[settings.dateFormat],
      },
    },
  }
}

const migrateVisibleColumns = (columns: string[] | null): TableColumn[] => {
  if (!columns) return BASE_SETTINGS.visibleColumns
  // Migrate 'expectations' -> 'target' for backwards compatibility
  return columns.map(col => col === 'expectations' ? 'target' : col) as TableColumn[]
}

const mapRow = (
  row: any
): Omit<UserSettings, 'displayName' | 'onboardingCompleted' | 'onboardingGoal' | 'currentSearchStage' | 'onboardingResponses' | 'productTourCompleted'> => ({
  autoConvertRates: row?.auto_convert_rates ?? BASE_SETTINGS.autoConvertRates,
  emailIntegrationEnabled: row?.email_integration_enabled ?? BASE_SETTINGS.emailIntegrationEnabled,
  autoGhostEnabled: row?.auto_ghost_enabled ?? BASE_SETTINGS.autoGhostEnabled,
  autoArchiveEnabled: row?.auto_archive_enabled ?? BASE_SETTINGS.autoArchiveEnabled,
  currencyConversionEnabled: row?.currency_conversion_enabled ?? BASE_SETTINGS.currencyConversionEnabled,
  targetCurrency: (row?.target_currency as Currency) ?? detectCurrencyFromLocale(),
  salaryDisplayFormat: (row?.salary_display_format as SalaryDisplayFormat) ?? BASE_SETTINGS.salaryDisplayFormat,
  defaultDisplayMode: (row?.default_display_mode as DisplayMode) ?? BASE_SETTINGS.defaultDisplayMode,
  dateFormat: (row?.date_format as DateFormat) ?? BASE_SETTINGS.dateFormat,
  visibleColumns: migrateVisibleColumns(row?.visible_columns),
  resumeConsentGiven: row?.resume_consent_given ?? BASE_SETTINGS.resumeConsentGiven,
  resumeConsentDate: row?.resume_consent_date ?? BASE_SETTINGS.resumeConsentDate,
  themeMode: (row?.theme_mode as ThemeMode) ?? BASE_SETTINGS.themeMode,
})

/**
 * Get default settings with smart currency detection based on browser locale
 */
export const getDefaultSettings = (): UserSettings => ({
  ...BASE_SETTINGS,
  targetCurrency: detectCurrencyFromLocale(),
})

export const fetchUserSettings = async (userId: string): Promise<UserSettings> => {
  const { data, error } = await supabase
    .from('user_settings')
    .select('auto_convert_rates, email_integration_enabled, auto_ghost_enabled, auto_archive_enabled, currency_conversion_enabled, target_currency, salary_display_format, default_display_mode, date_format, visible_columns, resume_consent_given, resume_consent_date, theme_mode')
    .eq('user_id', userId)
    .maybeSingle()

  if (error && error.code !== 'PGRST116') {
    // PGRST116 -> no rows returned
    throw error
  }

  // Fetch profile-backed settings
  let displayName: string | null = null
  let onboardingCompleted = false
  let onboardingGoal: OnboardingGoal | null = null
  let currentSearchStage: CurrentSearchStage | null = null
  let onboardingResponses: OnboardingResponses | null = null
  let productTourCompleted = false
  const { data: profileData } = await supabase
    .from('user_profile')
    .select('display_name, onboarding_completed, onboarding_goal, current_search_stage, onboarding_responses, product_tour_completed')
    .eq('user_id', userId)
    .maybeSingle()
  
  if (profileData) {
    displayName = profileData.display_name
    onboardingCompleted = profileData.onboarding_completed ?? false
    onboardingGoal = (profileData.onboarding_goal as OnboardingGoal | null) ?? null
    currentSearchStage = (profileData.current_search_stage as CurrentSearchStage | null) ?? null
    onboardingResponses = (profileData.onboarding_responses as OnboardingResponses | null) ?? null
    productTourCompleted = profileData.product_tour_completed ?? false
  }

  if (!data) {
    const defaultSettings = getDefaultSettings()
    return { ...defaultSettings, displayName, onboardingCompleted, onboardingGoal, currentSearchStage, onboardingResponses, productTourCompleted }
  }
  
  const settings = mapRow(data)
  return { ...settings, displayName, onboardingCompleted, onboardingGoal, currentSearchStage, onboardingResponses, productTourCompleted }
}

export const saveUserSettings = async (userId: string, settings: UserSettings) => {
  const payload = {
    user_id: userId,
    auto_convert_rates: settings.autoConvertRates,
    email_integration_enabled: settings.emailIntegrationEnabled,
    auto_ghost_enabled: settings.autoGhostEnabled,
    auto_archive_enabled: settings.autoArchiveEnabled,
    currency_conversion_enabled: settings.currencyConversionEnabled,
    target_currency: settings.targetCurrency,
    salary_display_format: settings.salaryDisplayFormat,
    default_display_mode: settings.defaultDisplayMode,
    date_format: settings.dateFormat,
    visible_columns: settings.visibleColumns,
    resume_consent_given: settings.resumeConsentGiven,
    resume_consent_date: settings.resumeConsentDate,
    theme_mode: settings.themeMode,
    updated_at: new Date().toISOString(),
  }
  const { error } = await supabase.from('user_settings').upsert(payload, { onConflict: 'user_id' })
  if (error) throw error

  // Save display_name to user_profile
  const { error: profileError } = await supabase
    .from('user_profile')
    .upsert({
      user_id: userId,
      display_name: settings.displayName,
      onboarding_completed: settings.onboardingCompleted,
      onboarding_goal: settings.onboardingGoal,
      current_search_stage: settings.currentSearchStage,
      onboarding_responses: buildOnboardingResponses(settings),
      product_tour_completed: settings.productTourCompleted,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' })
  
  if (profileError) throw profileError
}

/**
 * Save user's consent for resume data processing
 */
export const saveResumeConsent = async (userId: string) => {
  const { error } = await supabase
    .from('user_settings')
    .upsert({
      user_id: userId,
      resume_consent_given: true,
      resume_consent_date: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' })
  
  if (error) throw error
}
